import type {
  LivePreviewProblem,
  LivePreviewStatus,
  PreviewDisplayState,
  PreviewLayer,
  PreviewRenderState,
} from './const';
import { useChatStore } from '@/stores/chat';
import { useProjectStore } from '@/stores/project';
import { ProjectStatus } from '../project/types';
import { type PendingPreviewNavigation, usePreviewStore } from './index';

export interface PreviewRenderInput {
  projectStatus: ProjectStatus;
  livePreviewStatus: LivePreviewStatus;
  livePreviewProblem?: LivePreviewProblem;
  targetLayer: PreviewLayer;
  sandboxUrl: string;
  preReleaseUrl: string;
  sandboxIframeReady: boolean;
  preReleaseReady: boolean;
  preReleaseIframeReady: boolean;
  pendingPreviewNavigation?: PendingPreviewNavigation | null;
  iframeLoadId?: string;
  initialGenerationRevealPending?: boolean;
  deliveryExpectedVersionId?: string;
  isStreaming?: boolean;
}

export function shouldHoldGenerationWaitingFrame(input: {
  projectStatus: ProjectStatus;
  initialGenerationRevealPending?: boolean;
  isStreaming?: boolean;
}) {
  return Boolean(
    input.isStreaming
    && (
      input.projectStatus === ProjectStatus.GENERATING
      || input.initialGenerationRevealPending
    ),
  );
}

export function shouldFallbackToStable(problem?: LivePreviewProblem) {
  return problem === 'client-unavailable' || problem === 'blank-screen' || problem === 'sandbox-unavailable';
}

export function isPreservingLiveNavigation(input: Pick<
  PreviewRenderInput,
  | 'targetLayer'
  | 'sandboxUrl'
  | 'livePreviewStatus'
  | 'livePreviewProblem'
  | 'deliveryExpectedVersionId'
  | 'pendingPreviewNavigation'
  | 'iframeLoadId'
>) {
  const navigation = input.pendingPreviewNavigation;
  return Boolean(
    input.targetLayer === 'live'
    && input.sandboxUrl
    && (input.livePreviewStatus === 'connecting' || (navigation?.mode === 'client' && input.livePreviewStatus === 'ready'))
    && !input.livePreviewProblem
    && !input.deliveryExpectedVersionId
    && navigation?.targetLayer === 'live'
    && (navigation.mode === 'client' || navigation.preserveLiveLayer)
    && navigation.iframeLoadId
    && navigation.iframeLoadId === input.iframeLoadId,
  );
}

function getGeneratedDisplayState(input: PreviewRenderInput): PreviewDisplayState {
  const {
    livePreviewStatus,
    livePreviewProblem,
    targetLayer,
    sandboxUrl,
    sandboxIframeReady,
    preReleaseReady,
    preReleaseIframeReady,
    preReleaseUrl,
    pendingPreviewNavigation,
    deliveryExpectedVersionId,
  } = input;
  const stableReady = Boolean(preReleaseUrl) && preReleaseReady && preReleaseIframeReady;
  const stableVisibleDuringNavigation = Boolean(preReleaseUrl)
    && Boolean(pendingPreviewNavigation)
    && !deliveryExpectedVersionId;
  const canDisplayStable = stableReady || stableVisibleDuringNavigation;
  const liveReady = livePreviewStatus === 'ready' && sandboxIframeReady;

  if (input.pendingPreviewNavigation?.mode === 'client' && isPreservingLiveNavigation(input)) {
    return { type: 'loading', targetIframeType: 'live', displayedIframeType: 'live' };
  }

  if (targetLayer === 'live' && liveReady) {
    return { type: 'ready', displayedIframeType: 'live' };
  }

  if (livePreviewStatus === 'error') {
    const severeRuntimeError = shouldFallbackToStable(livePreviewProblem);
    if (severeRuntimeError && preReleaseUrl && !canDisplayStable) {
      return {
        type: 'loading',
        targetIframeType: 'stable',
        displayedIframeType: null,
      };
    }

    const displayedIframeType = severeRuntimeError
      ? canDisplayStable
        ? 'stable'
        : null
      : targetLayer === 'stable' && canDisplayStable
        ? 'stable'
        : sandboxUrl
        ? 'live'
        : canDisplayStable
          ? 'stable'
          : null;
    return {
      type: 'error',
      failedIframeType: 'live',
      displayedIframeType,
    };
  }

  if (livePreviewStatus === 'connecting') {
    return {
      type: 'loading',
      targetIframeType: 'live',
      displayedIframeType: isPreservingLiveNavigation(input) ? 'live' : canDisplayStable ? 'stable' : null,
    };
  }

  if (canDisplayStable) {
    return { type: 'ready', displayedIframeType: 'stable' };
  }

  return {
    type: 'loading',
    targetIframeType: 'stable',
    displayedIframeType: null,
  };
}

export function getPreviewRenderState(input: PreviewRenderInput): PreviewRenderState {
  const shouldDisplayGenerating =
    input.projectStatus === ProjectStatus.GENERATING || Boolean(input.initialGenerationRevealPending);
  const shouldHoldWaitingFrame = shouldHoldGenerationWaitingFrame(input);
  const isGenerated = input.projectStatus === ProjectStatus.GENERATED;
  const mountLive = shouldDisplayGenerating || input.livePreviewStatus !== 'idle';
  const useStableFallback = shouldFallbackToStable(input.livePreviewProblem);
  const shouldMountDeliveryStable = Boolean(input.deliveryExpectedVersionId)
    && input.targetLayer === 'stable';
  const shouldMountStable =
    shouldMountDeliveryStable ||
    useStableFallback ||
    (!shouldDisplayGenerating && input.pendingPreviewNavigation && !input.deliveryExpectedVersionId) ||
    (!shouldDisplayGenerating && input.preReleaseIframeReady) ||
    (!shouldDisplayGenerating && input.livePreviewStatus === 'connecting') ||
    input.livePreviewStatus === 'idle' ||
    (input.targetLayer === 'stable' && (input.livePreviewStatus !== 'error' || !input.sandboxUrl));
  const mountStable = Boolean(input.preReleaseUrl)
    && shouldMountStable
    && (isGenerated || shouldMountDeliveryStable);
  const deliveryStableReady = shouldMountDeliveryStable
    && Boolean(input.preReleaseUrl)
    && input.preReleaseReady
    && input.preReleaseIframeReady;

  if (shouldDisplayGenerating) {
    if (deliveryStableReady) {
      return {
        display: { type: 'ready', displayedIframeType: 'stable' },
        mountLive,
        mountStable,
      };
    }
    return {
      display:
        input.livePreviewStatus === 'error' && !shouldHoldWaitingFrame && !shouldMountDeliveryStable
          ? {
              type: 'error',
              failedIframeType: 'live',
              displayedIframeType: null,
            }
          : { type: 'generating', displayedIframeType: null },
      mountLive,
      // Keep the exact version iframe hidden behind WaitingFrame so its
      // site-runtime readiness can unlock the third stage. It must not be
      // routed through the live Sandbox iframe just to get mounted early.
      mountStable,
    };
  }

  if (isGenerated) {
    return {
      display: getGeneratedDisplayState(input),
      mountLive,
      mountStable,
    };
  }

  return {
    display: {
      type: 'loading',
      targetIframeType: 'live',
      displayedIframeType: null,
    },
    mountLive,
    mountStable: false,
  };
}

/** Preview UI 的唯一展示与 iframe 挂载策略入口。 */
export function usePreviewRenderState(projectStatusOverride?: ProjectStatus) {
  const isStreaming = useChatStore(state => state.isStreaming);
  const storedProjectStatus = useProjectStore(state => state.status);
  const livePreviewStatus = usePreviewStore(state => state.livePreviewStatus);
  const livePreviewProblem = usePreviewStore(state => state.livePreviewProblem);
  const targetLayer = usePreviewStore(state => state.targetLayer);
  const sandboxUrl = usePreviewStore(state => state.sandboxUrl);
  const preReleaseUrl = usePreviewStore(state => state.preReleaseUrl);
  const sandboxIframeReady = usePreviewStore(state => state.sandboxIframeReady);
  const preReleaseReady = usePreviewStore(state => state.preReleaseReady);
  const preReleaseIframeReady = usePreviewStore(state => state.preReleaseIframeReady);
  const pendingPreviewNavigation = usePreviewStore(state => state.pendingPreviewNavigation);
  const iframeLoadId = usePreviewStore(state => state.iframeLoadId);
  const initialGenerationRevealPending = usePreviewStore(state => state.initialGenerationRevealPending);
  const deliveryExpectedVersionId = usePreviewStore(state => state.deliveryExpectedVersionId);

  return getPreviewRenderState({
    projectStatus: projectStatusOverride ?? storedProjectStatus,
    livePreviewStatus,
    livePreviewProblem,
    targetLayer,
    sandboxUrl,
    preReleaseUrl,
    sandboxIframeReady,
    preReleaseReady,
    preReleaseIframeReady,
    pendingPreviewNavigation,
    iframeLoadId,
    initialGenerationRevealPending,
    deliveryExpectedVersionId,
    isStreaming,
  });
}
